/**
 * /university/skills — catalog grid.
 *
 * Notion-strict: page header → search/filter bar → grid. The search is
 * client-side reactive; the filter selects re-trigger the query with
 * server-side narrowing. Sort selector controls the order.
 */
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ChevronLeft, Loader2, Search } from 'lucide-react';
import * as React from 'react';
import { z } from 'zod';
import { Input } from '../../../../components/ui/input';
import { SkillCard } from '../../../../components/university/skill-card';
import { type ListSkillsParams, learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';

const skillsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(['all', 'private', 'candidate', 'exportable', 'published']).optional(),
  sort: z.enum(['updated', 'effectiveness', 'attempts', 'name']).optional(),
});

function SkillsCatalogRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [localQ, setLocalQ] = React.useState(search.q ?? '');

  // Debounce the search input so we don't fire on every keystroke.
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      void navigate({
        search: (prev) => ({ ...prev, q: localQ || undefined }),
        replace: true,
      });
    }, 240);
    return () => window.clearTimeout(handle);
  }, [localQ, navigate]);

  const queryParams: ListSkillsParams = React.useMemo(() => {
    const params: ListSkillsParams = {};
    if (search.q) params.q = search.q;
    if (search.status && search.status !== 'all') params.status = search.status;
    if (search.sort) params.sort = search.sort;
    return params;
  }, [search.q, search.status, search.sort]);

  const skillsQ = useQuery({
    queryKey: learningKeys.skillsList(queryParams),
    queryFn: () => learningApi.listSkills(queryParams),
    staleTime: 30_000,
  });

  const skills = skillsQ.data?.skills ?? [];

  return (
    <section className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/university"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to University
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
            Skills
            <span className="ml-2 text-[12px] font-normal text-muted-foreground">
              {skills.length} {skills.length === 1 ? 'skill' : 'skills'}
            </span>
          </h1>
          {skillsQ.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
            </span>
          ) : null}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={localQ}
            onChange={(e) => setLocalQ(e.target.value)}
            placeholder="Search skills by name, key, description…"
            className="h-9 pl-8 text-[13px]"
            aria-label="Search skills"
          />
        </div>
        <FilterSelect
          label="Status"
          value={search.status ?? 'all'}
          onChange={(value) =>
            navigate({
              search: (prev) => ({
                ...prev,
                status: value === 'all' ? undefined : (value as typeof search.status),
              }),
              replace: true,
            })
          }
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'private', label: 'Private' },
            { value: 'candidate', label: 'Candidate' },
            { value: 'exportable', label: 'Exportable' },
            { value: 'published', label: 'Published' },
          ]}
        />
        <FilterSelect
          label="Sort by"
          value={search.sort ?? 'updated'}
          onChange={(value) =>
            navigate({
              search: (prev) => ({ ...prev, sort: value as typeof search.sort }),
              replace: true,
            })
          }
          options={[
            { value: 'updated', label: 'Recently updated' },
            { value: 'effectiveness', label: 'Effectiveness' },
            { value: 'attempts', label: 'Attempts' },
            { value: 'name', label: 'Name (A-Z)' },
          ]}
        />
      </div>

      {skillsQ.isPending ? (
        <div className="px-2 py-4 text-[12px] text-muted-foreground">Loading skills…</div>
      ) : skillsQ.isError ? (
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Failed to load skills: {(skillsQ.error as Error).message}
        </div>
      ) : skills.length === 0 ? (
        <div className="px-2 py-6 text-center text-[12px] text-muted-foreground/70">
          No skills match the current filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {skills.map((skill) => (
            <SkillCard key={skill.skillId} skill={skill} />
          ))}
        </div>
      )}
    </section>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
}

function FilterSelect({ label, value, onChange, options }: FilterSelectProps) {
  return (
    <label className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-9 rounded border border-border bg-background px-2 text-[12px]',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        )}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export const Route = createFileRoute('/_authenticated/university/skills/')({
  validateSearch: skillsSearchSchema,
  component: SkillsCatalogRoute,
});
